import { Shape, ShapeOptions } from "../Shape";
import { SVGParsedPathData, createSVGElement } from "../../../svg/";
import { YZCanvasContext } from "../canvas";
import { extractNumber } from "@zyzgroup/core-common";

export type TextShapeOptions = {
  text: string;
} & ShapeOptions;

export class TextShape extends Shape {
  public text: string;
  public width = 0;
  public height = 0;

  constructor(options: TextShapeOptions) {
    super(options);
    this.text = options.text;

    this.setAttr({
      // default attributes
      fontSize: "10px",
      fontFamily: `system-ui, Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif`,
      // see https://nolanlawson.com/2022/04/08/the-struggle-of-using-native-emoji-on-the-web/
      emojiFontFamily:
        '"Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji", "EmojiOne Color", "Android Emoji", "Twemoji Mozilla", "system emoji", sans-serif',
      fill: "#666666",
      stroke: "none",
      "alignment-baseline": "middle",
      textAnchor: "start", // start | end
      // custom attributes
      ...this.attrs
    });

    const fontSize = extractNumber(this.getAttr("fontSize"), 10).number;
    const fontWeight = extractNumber(this.getAttr("fontWeight"), 400).number;
    const mt = TextShape.measureText(this.text, fontSize, fontWeight);
    this.width = mt.width;
    this.height = mt.height;
  }

  protected cloneShape(): TextShape {
    const shape = new TextShape({
      ...super.getBaseOptions(),
      text: this.text
    });
    return shape;
  }

  public toSVGEl(): SVGElement {
    const el = createSVGElement("text");
    el.setAttribute("x", "0");
    el.setAttribute("y", "0");
    el.style.alignmentBaseline = "middle";
    const texts = this.text.split("\n");
    if (texts.length <= 1) {
      el.innerHTML = this.text;
    } else {
      const lineHeight = this.getAttr("line-height", "1.2em") as string;
      texts.forEach((t, index) => {
        if (t.trim()) {
          const tspan = createSVGElement("tspan");
          tspan.setAttribute("x", "0");
          tspan.setAttribute("dy", index > 0 ? lineHeight : "0");
          tspan.innerHTML = t.trim();
          tspan.style.alignmentBaseline = "middle";
          el.append(tspan);
        }
      });
    }
    return el;
  }

  public toSVGPathData(): SVGParsedPathData[] {
    return [];
  }

  public toCanvasContext(ctx: YZCanvasContext) {
    const texts = this.text.split("\n");
    if (texts.length <= 1) {
      ctx.fillText(this.text, 0, 0);
    } else {
      const metrics: TextMetrics = ctx.measureText(this.text);
      const height =
        metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
      const lineHeight =
        height * parseFloat(this.getAttr("line-height", "1.2em") as string);
      let y = 0;
      texts.forEach((t, index) => {
        if (t.trim()) {
          y += index > 0 ? lineHeight : 0;
          ctx.fillText(t.trim(), 0, y);
        }
      });
    }
  }

  public static measureText(
    text: string,
    fontSize = 12,
    fontWeight = 400
  ): { width: number; height: number } {
    const span = document.createElement("span");
    span.style.visibility = "hidden";
    span.style.margin = "0";
    span.style.padding = "0";
    span.style.inset = "0";
    span.style.whiteSpace = "nowrap";
    span.style.overflow = "visible";
    span.style.fontSize = fontSize + "px";
    span.style.fontWeight = fontWeight.toString();
    span.innerText = text;
    document.body.appendChild(span);
    const width = span.offsetWidth;
    const height = span.offsetHeight;
    document.body.removeChild(span);
    return { width, height };
    // const canvas = new OffscreenCanvas(fontSize, fontSize);
    // const ctx = canvas.getContext("2d");
    // const metrics: TextMetrics = ctx.measureText(this.text);
  }

  /**
   * 获取固定宽度的字符串，如果传入的text的宽度不够width,则返回原字符串
   * @text   将要被提取的原字符串
   * @width  想要提取的字符串长度
   * @fontSize  字符串显示时的字符大小，支持>=12
   * @fontWeight  字符串显示时的字符粗细
   * @isNeedEllipsis   当text的宽度大于width时是否需要省略号
   */
  public static getFixedWidthText(
    text: string,
    width: number,
    fontSize = 12,
    fontWeight = 400,
    gap = "..."
  ) {
    let returnText = "";
    if (!text || width < fontSize) {
      return returnText;
    }
    const arr = text.split("");
    let nowWidth = gap
      ? TextShape.measureText(gap, fontSize, fontWeight).width
      : 0;
    for (let i = 0, l = arr.length; i < l; i++) {
      nowWidth += TextShape.measureText(arr[i], fontSize, fontWeight).width;
      if (nowWidth > width) {
        if (gap) {
          returnText += gap;
        }
        break;
      } else {
        returnText += arr[i];
      }
    }
    return returnText;
  }
}

export default TextShape;
